import { SpeciAction, SpeciEditor } from '@shared/components/speci/speci-editor/speci-editor'
import { useNavigation } from '@/hooks/use-navigation'
import { useOutletContext, useParams } from 'react-router'
import { useEffect, useState } from 'react'
import {
  useAuthenticatedGraphMutation,
  useAuthenticatedGraphQuery,
} from '@/hooks/use-authenticated-query'
import { GetSpeciStationDataGql } from '@/graphql/queries/speci/get-speci-station-data.graphql'
import { SpeciData, SpeciStation } from '@shared/components/speci/speci-types'
import { GetSpeciGql } from '@/graphql/queries/speci/get-speci.graphql'
import {
  showErrorNotification,
  showSuccessNotification,
} from '@shared/helpers/notifications/notifications'
import { SetSpeciGql } from '@/graphql/queries/speci/speci-upsert.graphql'
import { OutletContextProps } from '@shared/components/app-shell/app-shell'
import { EditSpeciSidebar } from '@/pages/stations/speci/edit-speci/edit-speci-sidebar'
import { EmailSpeciModal } from '@/components/speci/email-speci-modal/email-speci-modal'

interface EditSpeciPageProps {
  action?: SpeciAction
}

export function EditSpeciPage({ action = SpeciAction.EDIT }: EditSpeciPageProps) {
  const { navigateToStationSpecis } = useNavigation()
  const { stationRecordNumber, speciId } = useParams()
  const [speciStation, setSpeciStation] = useState<SpeciStation | undefined>(undefined)
  const outletContext: OutletContextProps = useOutletContext()

  const goToStationSpeci = () => navigateToStationSpecis(stationRecordNumber)

  useEffect(() => {
    // If no speciId is present in the URL, navigate to the station speci page
    if (!speciId && stationRecordNumber) {
      goToStationSpeci()
    }
  }, [speciId, stationRecordNumber])

  // Update sidebar content
  useEffect(() => {
    if (outletContext) {
      outletContext.setSidebarComponent(<EditSpeciSidebar />)
      return () => outletContext.setSidebarComponent(null) // Clear sidebar on unmount
    }
  }, [])

  // Fetch additional station data
  const { data: stationData, isLoading: stationDataLoading } = useAuthenticatedGraphQuery({
    queryKey: ['speciStationDetails', stationRecordNumber],
    gql: GetSpeciStationDataGql,
    queryParams: { recordNumber: stationRecordNumber },
  })

  useEffect(() => {
    // Set speciStation object in the page state based on the stationData response
    if (stationData && stationData?.stationDetailsForSpeci) {
      setSpeciStation({
        recordNumber: stationRecordNumber ?? '',
        brand: stationData?.stationDetailsForSpeci.brand,
        tradingName: stationData?.stationDetailsForSpeci.tradingName,
      })
    }
  }, [stationData])

  // Fetch the wool specification data
  const {
    data: speciData,
    isFetching,
    isStale,
  } = useAuthenticatedGraphQuery({
    queryKey: ['getSpeci', speciId],
    gql: GetSpeciGql,
    queryParams: { speciId },
    enabled: !!action && action === SpeciAction.EDIT && !!speciId,
  })

  // Mutation to update the wool specification
  const updateSpeciMutation = useAuthenticatedGraphMutation({
    gql: SetSpeciGql,
    gqlKey: 'setSpeci',
  })

  const handleUpdateSpeci = async (
    speci: SpeciData,
    stationRecordNumber: string,
    submitPending: boolean
  ) => {
    try {
      await updateSpeciMutation.mutateAsync({
        speci,
        stationRecordNumber,
      })
      if (!submitPending) {
        showSuccessNotification('Wool Specification successfully updated')
      }
    } catch (error) {
      showErrorNotification('Failed to update wool specification', { autoClose: true })
      throw error
    }
  }

  if (!speciStation || !speciId) {
    return null
  }

  return (
    <SpeciEditor
      action={action}
      station={speciStation}
      stationDetailsForSpeci={stationData?.stationDetailsForSpeci ?? undefined}
      speciId={speciId}
      speciData={speciData?.speci?.speci ?? undefined}
      stationDataLoading={stationDataLoading}
      speciDataIsStale={isStale}
      speciDataIsFetching={isFetching}
      onUpdateSpeci={handleUpdateSpeci}
      onNavigateToSpeciHomepage={goToStationSpeci}
      submitTitle='Send to Wool Store'
      renderBackButton
      useInternalInstructions
      requireInstructions
      renderSubmitModal={({ isModalOpen, closeModal }) => (
        <EmailSpeciModal
          speciId={speciId}
          isModalOpen={isModalOpen}
          closeModal={closeModal}
          brand={stationData?.stationDetailsForSpeci?.brand || ''}
          successNavigate={goToStationSpeci}
        />
      )}
      displayCertificationStatus
    />
  )
}
